import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import type { ProposalCollaborationEnvelope } from "work/proposals/negotiation/proposal-collaboration-contract";
import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import { showProposalCommandError } from "work/proposals/_views/_shared/proposal-command-error";
import {
  describeProposalMissingItemSaveFailure,
  readProposalMissingItemTarget,
} from "work/proposals/negotiation/proposal-missing-item-save-failure";

export const REFRESH_AND_REAPPLY_LABEL = "Refresh and re-apply my changes";

type ReapplyingCollaboration = Readonly<{
  effectiveProposalJSON?: ProposalJSON;
  refreshAndReapplyPendingChanges: () => Promise<ProposalCollaborationEnvelope | undefined>;
}>;

/**
 * Reports a save refused because it changed an item the proposal does not hold, and offers the way
 * out: refresh onto the latest proposal and save the rest again (#6612).
 *
 * Answers false for any other failure, so the page reports it as it always has. The draft page and
 * the active page share it, so the two cannot tell the reader different things.
 *
 * Module-level, so none of it is compiled into the save routines that call it. See app/CLAUDE.md.
 */
export function reportProposalMissingItemSaveFailure(
  error: unknown,
  collaboration: ReapplyingCollaboration,
): boolean {
  const target = readProposalMissingItemTarget(error);
  if (!target) return false;

  showErrorSnackbar(
    error,
    {
      doing: "saving your proposal changes",
      operation: "proposal.save",
      message: describeProposalMissingItemSaveFailure(target, collaboration.effectiveProposalJSON),
      recovery: { label: REFRESH_AND_REAPPLY_LABEL, onRecover: () => void reapply(collaboration) },
    },
    { persist: true },
  );
  return true;
}

async function reapply(collaboration: ReapplyingCollaboration): Promise<void> {
  try {
    await collaboration.refreshAndReapplyPendingChanges();
    enqueueSnackbar("Your changes are saved.", { variant: "success" });
  } catch (error) {
    console.error("Failed to re-apply proposal changes", error);
    if (reportProposalMissingItemSaveFailure(error, collaboration)) return;
    showProposalCommandError(error, "re-applying your proposal changes", "proposal.reapply");
  }
}
